import { formatArea } from 'features/kestrelViewer/utils/unitConversionUtils'
import { useGeckoLocale } from 'features/locale'
import React, { FC } from 'react'
import styled from 'styled-components'
import { KVAnnotation, KVUnitType } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  background-color: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  border-radius: 4px;
  padding: 0.75rem;
`

const DimensionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DimensionLabel = styled.div`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 500;
  letter-spacing: 0.05rem;
  min-width: 50px;
`

const DimensionValue = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.activeText};
  font-weight: 600;
`

const StyledInput = styled.input<{ $isFocused: boolean }>`
  flex: 1;
  padding: 0.4rem;
  background-color: ${({ theme }) => theme.forecast.background3};
  border: 1px solid
    ${({ theme, $isFocused }) =>
      $isFocused ? theme.forecast.primary : theme.forecast.border};
  border-radius: 4px;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.7rem;
  outline: none;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.border};
  }

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
    opacity: 0.6;
  }
`

interface G1N6DimensionsSectionProps {
  annotation: KVAnnotation
  currentUnit: KVUnitType
  setAnnotation: (annotation: KVAnnotation) => void
}

export const G1N6DimensionsSection: FC<G1N6DimensionsSectionProps> = ({
  annotation,
  currentUnit,
  setAnnotation,
}) => {
  const { locale } = useGeckoLocale()
  const [widthFocused, setWidthFocused] = React.useState(false)
  const [heightFocused, setHeightFocused] = React.useState(false)

  const isPolygon = annotation.geometry?.kind === 'polygon'
  const isPoint = annotation.geometry?.kind === 'point'
  const disabled = annotation.historical

  if (!isPolygon && !isPoint) return null

  const g1n6Anno = annotation.g1n6Annotation

  const handleChange = (field: 'width' | 'height', value: string) => {
    if (!g1n6Anno) return

    setAnnotation({
      ...annotation,
      g1n6Annotation: {
        ...g1n6Anno,
        [field]: value,
      },
    })
  }

  return (
    <Container>
      {isPolygon && (
        <>
          {annotation.area !== undefined && (
            <DimensionRow>
              <DimensionLabel>AREA</DimensionLabel>
              <DimensionValue>
                {formatArea(annotation.area, currentUnit, locale)}
              </DimensionValue>
            </DimensionRow>
          )}
          <DimensionRow>
            <DimensionLabel>WIDTH</DimensionLabel>
            <StyledInput
              type='text'
              value={g1n6Anno?.width || ''}
              onChange={(e) => handleChange('width', e.target.value)}
              onFocus={() => setWidthFocused(true)}
              onBlur={() => setWidthFocused(false)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              disabled={disabled}
              placeholder='Enter width'
              $isFocused={widthFocused}
            />
            <DimensionLabel>HEIGHT</DimensionLabel>
            <StyledInput
              type='text'
              value={g1n6Anno?.height || ''}
              onChange={(e) => handleChange('height', e.target.value)}
              onFocus={() => setHeightFocused(true)}
              onBlur={() => setHeightFocused(false)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              disabled={disabled}
              placeholder='Enter height'
              $isFocused={heightFocused}
            />
          </DimensionRow>
        </>
      )}

      {isPoint &&
        annotation.geometry &&
        'position' in annotation.geometry &&
        annotation.geometry.position && (
          <DimensionRow>
            <DimensionLabel>POSITION (X, Y, Z)</DimensionLabel>
            <DimensionValue>
              ({annotation.geometry.position.x.toFixed(3)},{' '}
              {annotation.geometry.position.y.toFixed(3)},{' '}
              {annotation.geometry.position.z.toFixed(3)})
            </DimensionValue>
          </DimensionRow>
        )}
    </Container>
  )
}
